// Import necessary React and UI components
import React, { useMemo } from "react"
import { Fragment } from "react/jsx-runtime"

import { useTranslation } from "react-i18next"

import { Grid } from "@mui/material"
import { Tooltip, Typography } from "@synapse-analytics/synapse-ui"

// Import custom color scheme that provides a 7-color repeating pattern
import { customRechartsColorScheme } from "../../utils/genericHelpers"

interface RenderLegendsProps {
  legends: Array<{ text: string; color: string }>
}

/**
 * Renders individual legend items with colored markers and text labels
 *
 * @component
 * @param {Object} props - Component props
 * @param {Array<{text: string, color: string}>} props.legends - Array of legend items to display
 * @returns {React.ReactElement} Fragment containing legend items
 *
 * @example
 * ```tsx
 * <RenderLegends legends={[
 *   { text: "Legend 1", color: "#ff0000" },
 *   { text: "Legend 2", color: "#00ff00" }
 * ]} />
 * ```
 */
function RenderLegends(props: Readonly<RenderLegendsProps>): React.ReactElement {
  const { legends } = props

  return (
    <Fragment>
      {/* Render each legend item with its marker and text */}
      {legends?.map((legend: { text: string; color: string }) => (
        <Grid item key={legend.text} display={"flex"}>
          {/* Colored square marker */}
          <Grid item alignContent={"end"}>
            <span
              key="chip"
              style={{
                display: "inline-flex",
                width: "14px",
                height: "14px",
                background: legend.color,
                borderRadius: "2px",
                marginRight: "4px",
              }}
            />
          </Grid>

          {/* Legend text with ellipsis overflow handling */}
          <Grid item xs width={"calc(100% - 20px)"}>
            <Typography variant={"a"} noWrap display="inline">
              {legend.text}
            </Typography>
          </Grid>
        </Grid>
      ))}
    </Fragment>
  )
}

/**
 * Props interface for the Legends component
 */
interface LegendsProps {
  /** Array of legend labels to display */
  legends: string[]

  /**
   * Optional number of legends to show before grouping remaining items as "+ More".
   * If specified, additional items will be shown in a tooltip.
   */
  slice?: number
}

/**
 * A component that renders a list of legend items with colored markers.
 * Each legend item consists of a colored square marker and a text label.
 * If a slice number is provided, it will show that many items and group
 * the rest under a "+ More" button with a tooltip.
 *
 * @component
 * @param {LegendsProps} props - The component props
 * @param {string[]} props.legends - Array of legend labels to display
 * @param {number} [props.slice] - Optional number of legends to show before grouping remaining items as "+ More"
 *
 * @example
 * ```tsx
 * <Legends
 *   legends={["Item 1", "Item 2", "Item 3"]}
 *   slice={2} // Will show first 2 items and "+ More" for the remaining item
 * />
 * ```
 *
 * @returns {React.ReactElement} A React element containing the legend items
 */
export const Legends = (props: LegendsProps): React.ReactElement => {
  const { t } = useTranslation("graphs")
  const { legends, slice } = props

  // Create legend objects with cycling colors from the custom scheme
  const adjustedLegends = useMemo(() => {
    return legends.map((legend, index) => {
      return { text: legend, color: customRechartsColorScheme()[index % 7]?.border ?? "" }
    })
  }, [legends])

  return (
    <Fragment>
      {/* Render visible legend items based on slice value */}
      <RenderLegends legends={adjustedLegends.slice(0, slice)} />

      {/* Render "+ More" tooltip if there are additional items beyond the slice */}
      {slice && adjustedLegends.length > slice && (
        <Tooltip
          placement="left"
          title={
            <Grid item xs display={"flex"} direction={"column"} gap={1}>
              {/* Render remaining legend items in tooltip */}
              <RenderLegends legends={adjustedLegends.slice(slice)} />
            </Grid>
          }
          style={{ backgroundColor: "var(--base-background-3)", borderColor: "var(--base-border-1)" }}
        >
          <Typography variant="p">{t("legends.more")}</Typography>
        </Tooltip>
      )}
    </Fragment>
  )
}
